import cn from 'classnames';
import { ComponentProps as CPops, ElementType as EType, ReactNode } from 'react';
import Tooltip from '../Tooltip/Tooltip';
import './Button.scss';

const DefaultTagType = 'div' as const;
type DefaultTagT = typeof DefaultTagType;
type buttonTypes = 'primary' | 'outline';

type OwnProps<T extends EType> = {
  children: ReactNode;
  isDisabled?: boolean;
  tag?: T;
  buttonType?: buttonTypes | '';
  className?: string;
  showSpinner?: boolean;
  iconOnly?: boolean;
  tooltipText?: string;
};

type ButtonProps<T extends EType> = OwnProps<T> & Omit<CPops<T>, keyof OwnProps<T>>;

export const Button = <T extends EType = DefaultTagT>({
  children,
  tag,
  className,
  buttonType = 'primary',
  isDisabled = false,
  showSpinner = false,
  iconOnly = false,
  tooltipText,
  ...otherProps
}: ButtonProps<T>) => {
  const ButtonTag = tag || DefaultTagType;

  const renderButton = () => (
    <ButtonTag
      className={cn('btn', buttonType, className, {
        disabled: isDisabled || showSpinner,
        'icon-only': iconOnly,
      })}
      {...otherProps}
    >
      <div className="btn-field">{children}</div>
      {showSpinner ? <div className="spinner"></div> : ''}
    </ButtonTag>
  );

  if (isDisabled && tooltipText) {
    return <Tooltip tooltipText={tooltipText}>{renderButton()}</Tooltip>;
  }

  return renderButton();
};

Button.displayName = 'Button';
